import type React from 'react';
import {useState} from 'react';
import {Alert, Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import {History} from '@mui/icons-material';
import {useOutletContext} from 'react-router';
import {isAxiosError} from 'axios';
import {useCategoryPlanQuery, usePlanActions} from '../../hooks/useCategoryPlan';
import {useCategoryRecordQuery} from '../../hooks/useCategoryRecord';
import type {ToolKey} from '../../types/strategyTypes';
import type {CategoryProfileOutletContext} from '../profileTypes';
import {GatewayCard} from '../strategy/rail/GatewayCard';
import {ReadinessCards} from '../strategy/rail/ReadinessCards';
import {PlanHistoryDialog} from '../strategy/PlanHistoryDialog';
import {ActionsSection} from '../strategy/sections/ActionsSection';
import {LeversAndOptionsSection} from '../strategy/sections/LeversAndOptionsSection';
import {PlanMetaSection} from '../strategy/sections/PlanMetaSection';
import {RisksSection} from '../strategy/sections/RisksSection';
import {RoadmapSection} from '../strategy/sections/RoadmapSection';
import {SummarySection} from '../strategy/sections/SummarySection';
import {TargetsSection} from '../strategy/sections/TargetsSection';
import {TOOLS_SECTION_ID, ToolsSection} from '../strategy/sections/ToolsSection';

/**
 * Strategy (spec 4.5): the Stage 4 category plan on the left, readiness and gateway approval on the right (below the
 * plan on narrow screens). The plan starts as a rule-based draft and saves section by section.
 */
export const StrategyTab: React.FC = () => {
    const {databagId, path, money} = useOutletContext<CategoryProfileOutletContext>();
    const {data: plan, isLoading, isError, error} = useCategoryPlanQuery(databagId, path);
    const {data: record} = useCategoryRecordQuery(databagId, path);
    const actions = usePlanActions(databagId, path);
    const [tool, setTool] = useState<ToolKey>('windmill');
    const [historyOpen, setHistoryOpen] = useState(false);
    /** Shows one analysis in Strategy formulation and scrolls to it (the "Edit in ..." links of later sections). */
    const openTool = (key: ToolKey) => {
        setTool(key);
        document.getElementById(TOOLS_SECTION_ID)?.scrollIntoView({behavior: 'smooth', block: 'start'});
    };

    if (isError) {
        const status = isAxiosError(error) ? error.response?.status : undefined;
        return <Alert severity={status === 404 ? 'info' : 'error'}>
            {status === 404 ? 'No plan for this category: it has no spend in this dataset.' : 'Could not load the category plan.'}
        </Alert>;
    }
    if (isLoading || !plan) return <Skeleton variant="rounded" height={480}/>;

    const props = {plan, actions, money, databagId, path};
    const people = record?.team.map(m => m.name) ?? [];

    return (
        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', lg: 'minmax(0, 1fr) 340px'}, gap: 2, alignItems: 'start'}}>
            <Stack gap={2} sx={{minWidth: 0}}>
                {plan.status === 'awaiting' ? (
                    <Alert severity="info">The plan is awaiting sign-off and cannot be changed. Withdraw the submission to edit it.</Alert>
                ) : !plan.canEdit ? (
                    <Alert severity="info">You can view this plan but not change it.</Alert>
                ) : !plan.exists && (
                    <Alert severity="info">
                        Every section is a first draft from this category&apos;s spend, suppliers, contracts and record.
                        Change anything: the plan saves as you go, and each change can be undone.
                    </Alert>
                )}
                <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                        {`Spend basis: ${plan.period}.`}
                        {plan.exists && ' Saved sections keep the figures they were drafted with; the checks on the right use this basis.'}
                    </Typography>
                    {plan.exists && (
                        <Button size="small" startIcon={<History/>} onClick={() => setHistoryOpen(true)} sx={{flexShrink: 0}}>
                            Change history
                        </Button>
                    )}
                </Stack>
                {historyOpen && <PlanHistoryDialog databagId={databagId} path={path} onClose={() => setHistoryOpen(false)}/>}
                <PlanMetaSection {...props}/>
                <SummarySection {...props}/>
                <ToolsSection {...props} selected={tool} onSelect={setTool}/>
                <LeversAndOptionsSection {...props} openTool={openTool}/>
                <RoadmapSection {...props} openTool={openTool}/>
                <TargetsSection {...props} openTool={openTool}/>
                <ActionsSection {...props} people={people}/>
                <RisksSection {...props}/>
            </Stack>
            <Stack gap={2} sx={{position: {lg: 'sticky'}, top: {lg: 16}}}>
                <GatewayCard {...props}/>
                <ReadinessCards {...props} databagId={databagId}/>
            </Stack>
        </Box>
    );
};
